import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Card, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, FormControlLabel, Grid, Icon, IconButton, InputLabel,
  LinearProgress, MenuItem, Switch, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tooltip,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { CLARO, coresDoTema } from "assets/coresTema";
import { errorAlert, infoAviso, successAlert } from "utils/Functions";
import useVersaoModelos from "./useVersaoModelos";
import { get, post, postFile, postUpload, postUploadFile } from "utils/WebService";

// Envio de Mala Direta (fase 1): o público por ficha (com a visibilidade do usuário), a mensagem (modelo, data de
// retorno, anexo) e o disparo, que vira um lote na fila do backend (MalaDiretaLoteService): envia no ritmo da conta de
// saída, grava cada envio e a ocorrência na ficha. A aba "Lotes enviados" acompanha, pausa, retoma, cancela e reenvia.

const SITUACAO_LOTE = {
  ENVIANDO: { label: "Enviando", cor: "info" },
  PAUSADO: { label: "Pausado", cor: "warning" },
  CONCLUIDO: { label: "Concluído", cor: "success" },
  CANCELADO: { label: "Cancelado", cor: "secondary" },
};

const SITUACAO_ENVIO = {
  PENDENTE: { label: "Na fila", cor: "info" },
  ENVIANDO: { label: "Enviando", cor: "info" },
  ENVIADO: { label: "Enviado", cor: "success" },
  ERRO: { label: "Erro", cor: "error" },
  SEM_EMAIL: { label: "Sem e-mail válido", cor: "secondary" },
  CANCELADO: { label: "Cancelado", cor: "secondary" },
};

// Entrega do envio (retorno): só nos lotes enviados depois da leitura dos avisos de não entrega
const ENTREGA = {
  ENTREGUE: { label: "Entregue", cor: "success" },
  AGUARDANDO: { label: "Aguardando retorno", cor: "info" },
  NAO_ENTREGUE: { label: "Não entregue", cor: "error" },
  PARCIAL: { label: "Entregue em parte", cor: "warning" },
};

const FILTRO_VAZIO = {
  clientes: [], carteiras: [], status: [], responsaveis: [], marcacoes: [],
  saldoMin: "", saldoMax: "", atrasoMin: "", atrasoMax: "", diasSemReceber: "",
};

const numero = (v) => (v === null || v === undefined ? 0 : Number(v)).toLocaleString("pt-BR");
const textoOuTraco = (v) => (v === null || v === undefined || v === "" ? "-" : v);
const escapar = (t) => String(t ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const numeroOuNulo = (v) => (v === "" || v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));

/** O filtro da tela no formato do backend (MalaDiretaService.Filtro), com o texto para o histórico do lote. */
function paraBackend(f) {
  const partes = [];
  const nomes = (lista) => lista.map((o) => o.label).join(", ");
  if (f.clientes.length) partes.push(`Cliente: ${nomes(f.clientes)}`);
  if (f.carteiras.length) partes.push(`Carteira: ${nomes(f.carteiras)}`);
  partes.push("Situação: todas, menos Devolvido");
  if (f.status.length) partes.push(`Status: ${nomes(f.status)}`);
  if (f.responsaveis.length) partes.push(`Responsável: ${nomes(f.responsaveis)}`);
  if (f.marcacoes.length) partes.push(`Marcação: ${nomes(f.marcacoes)}`);
  if (f.saldoMin !== "" || f.saldoMax !== "") partes.push(`Saldo da ficha: ${f.saldoMin || "0"} a ${f.saldoMax || "sem limite"}`);
  if (f.atrasoMin !== "" || f.atrasoMax !== "") partes.push(`Atraso: ${f.atrasoMin || "0"} a ${f.atrasoMax || "sem limite"} dias`);
  if (f.diasSemReceber !== "" && Number(f.diasSemReceber) > 0) partes.push(`Sem mala direta há ${f.diasSemReceber} dias`);
  return {
    clientes: f.clientes.map((o) => o.value),
    carteiras: f.carteiras.map((o) => o.value),
    status: f.status.map((o) => o.value),
    responsaveis: f.responsaveis.map((o) => o.value),
    marcacoes: f.marcacoes.map((o) => o.value),
    saldoMin: numeroOuNulo(f.saldoMin),
    saldoMax: numeroOuNulo(f.saldoMax),
    atrasoMin: numeroOuNulo(f.atrasoMin),
    atrasoMax: numeroOuNulo(f.atrasoMax),
    diasSemReceber: numeroOuNulo(f.diasSemReceber),
    descricao: partes.join(" · "),
  };
}

function Rotulo({ children }) {
  return <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>{children}</InputLabel>;
}

function Numero({ titulo, valor, cor, ajuda, cores }) {
  return (
    <MDBox sx={{ px: 1.5, py: 1, borderRadius: 1.5, border: `1px solid ${cores.divisoria}`, background: cores.painel, minWidth: 150, flex: "1 1 150px" }}>
      <MDTypography variant="caption" fontWeight="bold" sx={{ color: cores.textoSecundario, textTransform: "uppercase" }}>{titulo}</MDTypography>
      <MDTypography variant="h5" fontWeight="bold" color={cor || undefined} sx={cor ? undefined : { color: cores.texto }}>{numero(valor)}</MDTypography>
      {ajuda && <MDTypography variant="caption" display="block" sx={{ color: cores.textoSecundario }}>{ajuda}</MDTypography>}
    </MDBox>
  );
}

// ------------------------------------------------------------------ Novo envio

function NovoEnvio({ cores, aoCriarLote }) {
  const [opcoes, setOpcoes] = useState({ clientes: [], carteiras: [], status: [], responsaveis: [], marcacoes: [] });
  const [filtro, setFiltro] = useState(FILTRO_VAZIO);
  const [aplicado, setAplicado] = useState(null);
  const [resumo, setResumo] = useState(null);
  const [carregando, setCarregando] = useState(false);

  const [modelos, setModelos] = useState([]);
  const [modelo, setModelo] = useState(null);
  const [dataRetorno, setDataRetorno] = useState("");
  const [anexo, setAnexo] = useState(null);
  const anexoRef = useRef(null);
  const [conta, setConta] = useState(null);
  const [emailTeste, setEmailTeste] = useState("");
  // De onde sai: a conta padrão ou a caixa do responsável de cada ficha (só com a conta do Microsoft 365)
  const [remetente, setRemetente] = useState("CONTA");
  // Envio de teste: 1 e-mail por conta que enviaria o lote (a 1ª ficha de cada uma), só para o e-mail de teste; os
  // devedores da lista não recebem e nada vai para a ficha
  const [modoTeste, setModoTeste] = useState(false);
  const [ocupado, setOcupado] = useState("");
  const [preview, setPreview] = useState(null);

  const carregarModelos = (silencioso) => get("/mala-direta/modelos").then((r) => {
    const lista = (Array.isArray(r) ? r : []).map((m) => ({
      value: m.cvId, nome: m.ccNome, assunto: m.ccAssunto, carteira: m.cvIdCarteira ?? null, nomeCarteira: m.carteira,
      label: `${m.ccNome} · ${m.carteira || "universal"}`,
    }));
    setModelos(lista);
    // O modelo escolhido acompanha a alteração (nome, assunto, carteira); excluído, sai da escolha
    setModelo((atual) => (atual ? lista.find((m) => m.value === atual.value) || null : atual));
  }).catch((e) => { if (!silencioso) errorAlert(e?.message || "Erro ao carregar os modelos."); });

  useEffect(() => {
    carregarModelos(false);
    get("/mala-direta/conta").then(setConta).catch(() => null);
  }, []);

  // Modelo salvo ou excluído por outro usuário (ou na tela de Modelos): a lista se atualiza sozinha. O texto do e-mail
  // não fica na tela: a pré-visualização, o teste e o lote sempre leem o modelo como está gravado
  useVersaoModelos(() => carregarModelos(true));

  // Opções em cascata (MalaDiretaService.opcoes): cada campo só lista o que existe nas fichas que podem receber (sem as
  // devolvidas, só das carteiras e clientes que o usuário vê) e que passam pelos campos de cima: cliente > carteira >
  // status > responsável > marcação. O que deixa de existir sai da escolha. Na 1ª carga, com um cliente ou uma carteira
  // só liberada para o usuário, o campo já vem escolhido e travado.
  const [travados, setTravados] = useState({ clientes: null, carteiras: null });
  const primeiraCarga = useRef(true);
  const chaveCascata = JSON.stringify(["clientes", "carteiras", "status", "responsaveis"].map((c) => filtro[c].map((o) => o.value)));
  useEffect(() => {
    const [clientes, carteiras, status, responsaveis] = JSON.parse(chaveCascata);
    const t = setTimeout(() => {
      post("/mala-direta/opcoes", { filtro: { clientes, carteiras, status, responsaveis } })
        .then((r) => {
          const novas = { clientes: [], carteiras: [], status: [], responsaveis: [], marcacoes: [], ...r };
          setOpcoes(novas);
          if (primeiraCarga.current) {
            primeiraCarga.current = false;
            // Só trava o que é restrição do usuário (uma carteira das equipes dele, um cliente liberado), não o que é
            // único porque a base só tem fichas de um cliente ou de uma carteira
            const trava = {
              clientes: r?.travarCliente && novas.clientes.length === 1 ? novas.clientes[0] : null,
              carteiras: r?.travarCarteira && novas.carteiras.length === 1 ? novas.carteiras[0] : null,
            };
            setTravados(trava);
            if (trava.clientes || trava.carteiras) {
              setFiltro((f) => ({ ...f, clientes: trava.clientes ? [trava.clientes] : f.clientes, carteiras: trava.carteiras ? [trava.carteiras] : f.carteiras }));
            }
            return;
          }
          setFiltro((f) => {
            const manter = (chave) => f[chave].filter((o) => novas[chave].some((x) => String(x.value) === String(o.value)));
            const novo = { ...f };
            ["clientes", "carteiras", "status", "responsaveis", "marcacoes"].forEach((c) => { novo[c] = manter(c); });
            return JSON.stringify(novo) === JSON.stringify(f) ? f : novo;
          });
        })
        .catch(() => null);
    }, 250);
    return () => clearTimeout(t);
  }, [chaveCascata]);

  const mudar = (chave) => (valor) => setFiltro((f) => ({ ...f, [chave]: valor || [] }));
  const mudarTexto = (chave) => (e) => setFiltro((f) => ({ ...f, [chave]: e.target.value }));
  const alterado = aplicado && JSON.stringify(paraBackend(filtro)) !== JSON.stringify(aplicado);

  const aplicar = (f = filtro) => {
    const corpo = paraBackend(f);
    setCarregando(true);
    post("/mala-direta/destinatarios", { filtro: corpo })
      .then((r) => {
        setResumo(r);
        setAplicado(corpo);
      })
      .catch((e) => errorAlert(e?.message || "Erro ao montar o público."))
      .finally(() => setCarregando(false));
  };

  const limpar = () => {
    setFiltro({
      ...FILTRO_VAZIO,
      clientes: travados.clientes ? [travados.clientes] : [],
      carteiras: travados.carteiras ? [travados.carteiras] : [],
    });
    setResumo(null);
    setAplicado(null);
  };

  const pedido = () => ({ filtro: aplicado, modeloId: modelo?.value, parametros: { dataRetorno: dataRetorno || null }, remetente });

  const verPreview = (ficha) => {
    if (!modelo) return errorAlert("Escolha o modelo.");
    // A ficha da linha clicada mostra a rodinha no olho (a montagem pode levar alguns segundos)
    setOcupado(ficha ? `preview-${ficha}` : "preview");
    post("/mala-direta/preview", { ...pedido(), ficha: ficha || null })
      .then(setPreview)
      .catch((e) => errorAlert(e?.message || "Erro na pré-visualização."))
      .finally(() => setOcupado(""));
  };

  const multipart = (extra = {}) => {
    const fd = new FormData();
    fd.append("pedido", JSON.stringify({ ...pedido(), ...extra }));
    if (anexo) fd.append("anexo", anexo);
    return fd;
  };

  const enviarTeste = () => {
    if (!modelo) return errorAlert("Escolha o modelo.");
    setOcupado("teste");
    postUpload("/mala-direta/enviar-teste", multipart({ email: emailTeste }))
      .then((r) => {
        const envios = r.envios || [];
        const erros = envios.filter((e) => e.erro).length;
        const linhas = envios.map((e) => `<li style="margin-bottom:4px">Ficha #${e.ficha}${e.responsavel ? ` (${escapar(e.responsavel)})` : ""}: `
          + (e.erro ? `<b style="color:#d32f2f">não enviado</b> pela caixa ${escapar(e.de)}: ${escapar(e.erro)}`
            : `de <b>${escapar(e.de)}</b>`) + (e.noLugarDe ? ` (no lugar de ${escapar(e.noLugarDe)})` : "") + "</li>").join("");
        const texto = `${envios.length - erros} de ${envios.length} e-mail(s) de teste enviado(s) para <b>${escapar(r.para)}</b>:`
          + `<ul style="text-align:left;font-size:0.85rem;margin-top:8px">${linhas}</ul>`
          + (r.lote ? `<div style="font-size:0.8rem">Gravado como lote de teste nº ${r.lote}: aparece em "Lotes enviados" e, na Análise, com "Incluir envios de teste".</div>` : "");
        if (erros) errorAlert(texto);
        else successAlert(texto);
      })
      .catch((e) => errorAlert(e?.message || "O teste não foi enviado."))
      .finally(() => setOcupado(""));
  };

  const baixarValidacao = () => {
    setOcupado("validacao");
    postFile("/mala-direta/validacao", pedido(), "MALA_DIRETA_VALIDACAO.xlsx")
      .catch((e) => errorAlert(e?.message || "Erro ao gerar a planilha."))
      .finally(() => setOcupado(""));
  };

  const enviar = () => {
    if (!modelo) return errorAlert("Escolha o modelo.");
    const minutos = Math.ceil((resumo.comEmail || 0) / (conta?.limitePorMinuto || 30));
    const tempo = minutos < 60 ? `${minutos} min` : `${Math.floor(minutos / 60)} h ${minutos % 60} min`;
    infoAviso(
      `Enviar o modelo <b>${escapar(modelo.label)}</b> para <b>${numero(resumo.comEmail)}</b> ficha(s) (${numero(resumo.emails)} e-mail(s))?`
        + (resumo.semEmail ? `<br/>${numero(resumo.semEmail)} ficha(s) sem e-mail válido ficam de fora.` : "")
        + (remetente === "RESPONSAVEL"
          ? `<br/>Cada ficha sai pela caixa do seu responsável (${numero(resumo.responsaveis)} pessoa(s)).`
            + ((resumo.afastados || []).length
              ? `<br/>Afastados hoje: ${(resumo.afastados || []).map((a) => `${escapar(a.nome)} (${numero(a.fichas)} ficha(s))`).join(", ")}: as fichas deles saem pelas caixas dos outros responsáveis.`
              : "")
          : `<br/>Sai pela conta padrão (${escapar(conta?.email || "")}).`)
        + `<br/>No ritmo da conta, leva cerca de <b>${tempo}</b>. Cada envio grava uma ocorrência na ficha.`
        + (conta?.ambienteTeste ? `<br/><br/><b>Ambiente de teste:</b> os e-mails vão para ${escapar(conta.emailDeTeste || "(sem e-mail de teste)")}.` : ""),
      () => {
        setOcupado("envio");
        postUpload("/mala-direta/lotes", multipart())
          .then((r) => {
            successAlert(`Lote nº ${r.id} criado: ${numero(r.comEmail)} ficha(s) na fila. Acompanhe em "Lotes enviados".`);
            aoCriarLote(r.id);
          })
          .catch((e) => errorAlert(e?.message || "O lote não foi criado."))
          .finally(() => setOcupado(""));
      },
    );
  };

  const celulaCab = { fontWeight: "bold", fontSize: "0.75rem", backgroundColor: cores.painel, color: cores.texto, whiteSpace: "nowrap", py: 1 };
  const celula = { fontSize: "0.8rem", color: cores.texto, verticalAlign: "top", py: 1 };
  const select = (chave, lista, placeholder) => (
    <>
      <MDSelect name={chave} isMulti placeholder={placeholder} options={lista} getOptionLabel={(o) => o.label}
        value={filtro[chave]} onChange={mudar(chave)} isDisabled={!!travados[chave]} />
      {travados[chave] && (
        <MDTypography variant="caption" color="text" display="block" sx={{ px: 1, mt: 0.5 }}>
          {chave === "clientes" ? "Único cliente liberado para você." : "Única carteira liberada para você."}
        </MDTypography>
      )}
    </>
  );
  // Modelos: os universais e, com o público todo de uma carteira só, os dessa carteira (o backend confere de novo)
  const carteirasDoPublico = resumo && !alterado ? resumo.carteirasDoPublico || [] : null;
  const carteiraUnica = carteirasDoPublico
    ? (carteirasDoPublico.length === 1 ? carteirasDoPublico[0] : null)
    : (filtro.carteiras.length === 1 ? Number(filtro.carteiras[0].value) : null);
  const modelosDoPublico = modelos.filter((m) => m.carteira == null || m.carteira === carteiraUnica);
  const podeEnviar = resumo && !alterado && resumo.comEmail > 0 && modelo && conta?.configurada;
  const modeloServe = !modelo || modelosDoPublico.some((m) => m.value === modelo.value);
  useEffect(() => {
    if (!modeloServe) setModelo(null);
  }, [modeloServe]);
  const contaMicrosoft365 = conta?.tipo === "MICROSOFT365";
  // Quantos e-mails o teste manda: 1 pela conta padrão; pelo responsável, 1 por responsável (+ 1 pela conta padrão, se
  // houver fichas sem responsável com e-mail)
  const emailsNoTeste = !resumo || alterado ? null
    : remetente === "RESPONSAVEL" ? (resumo.responsaveis || 0) : (resumo.comEmail > 0 ? 1 : 0);
  // Pelo responsável não há conta padrão de reserva: toda ficha que recebe precisa de responsável com e-mail
  const pendenciasResponsavel = remetente === "RESPONSAVEL" && resumo && !alterado
    && ((resumo.fichasSemResponsavel || 0) > 0 || (resumo.responsaveisSemEmail || []).length > 0 || (resumo.semPresente || 0) > 0);
  // Responsáveis afastados hoje (Ferramentas > Usuários > Afastamentos): as fichas deles saem pelas caixas dos presentes
  const afastadosHoje = remetente === "RESPONSAVEL" && resumo && !alterado ? resumo.afastados || [] : [];

  return (
    <>
      <Card sx={{ px: 2, py: 1.5, mb: 2, background: cores.cartao }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={3}><Rotulo>Cliente</Rotulo>{select("clientes", opcoes.clientes, "Todos")}</Grid>
          <Grid item xs={12} md={3}><Rotulo>Carteira</Rotulo>{select("carteiras", opcoes.carteiras, "Todas")}</Grid>
          <Grid item xs={12} md={3}><Rotulo>Status</Rotulo>{select("status", opcoes.status, "Todos")}</Grid>
          <Grid item xs={12} md={3}><Rotulo>Responsável</Rotulo>{select("responsaveis", opcoes.responsaveis, "Todos")}</Grid>
          <Grid item xs={12} md={3}><Rotulo>Marcação especial</Rotulo>{select("marcacoes", opcoes.marcacoes, "Todas")}</Grid>
          <Grid item xs={12} md={3}>
            <Rotulo>Saldo em aberto da ficha (R$)</Rotulo>
            <MDBox display="flex" gap={1}>
              <MDInput size="small" type="number" placeholder="De" value={filtro.saldoMin} onChange={mudarTexto("saldoMin")} fullWidth />
              <MDInput size="small" type="number" placeholder="Até" value={filtro.saldoMax} onChange={mudarTexto("saldoMax")} fullWidth />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={3}>
            <Rotulo>Atraso (dias)</Rotulo>
            <MDBox display="flex" gap={1}>
              <MDInput size="small" type="number" placeholder="De" value={filtro.atrasoMin} onChange={mudarTexto("atrasoMin")} fullWidth />
              <MDInput size="small" type="number" placeholder="Até" value={filtro.atrasoMax} onChange={mudarTexto("atrasoMax")} fullWidth />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={3}>
            <Rotulo>Pular quem recebeu há (dias)</Rotulo>
            <MDInput size="small" type="number" placeholder="Em branco = não pula" value={filtro.diasSemReceber} onChange={mudarTexto("diasSemReceber")} fullWidth />
          </Grid>
          <Grid item xs={12} md={9}>
            <Rotulo>&nbsp;</Rotulo>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
              <MDTypography variant="caption" color="text" sx={{ px: 1 }}>
                Público por ficha: todas menos as devolvidas (como a Base de Dados), sem as de honorários, só das carteiras e clientes que você vê. Cada campo só mostra o que existe nas fichas dos campos anteriores. Atraso: dias desde o vencimento mais antigo em aberto da ficha.
              </MDTypography>
              <MDBox display="flex" gap={1}>
                <MDButton variant="contained" color="info" size="medium" sx={{ height: 38 }} onClick={limpar}>Limpar</MDButton>
                <MDButton variant="contained" color="info" size="medium" sx={{ height: 38 }} onClick={() => aplicar()} disabled={carregando}>
                  {carregando ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.75 }} /> : <Icon sx={{ mr: 0.5 }}>filter_alt</Icon>}
                  {carregando ? "Montando..." : "Aplicar"}
                </MDButton>
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
      </Card>

      {resumo && (
        <Card sx={{ p: 2, mb: 2, background: cores.cartao }}>
          {alterado && (
            <Alert severity="warning" sx={{ mb: 1.5, fontSize: "0.85rem" }}>Os filtros mudaram: clique em Aplicar para atualizar o público antes de enviar.</Alert>
          )}
          <MDBox display="flex" gap={1.5} flexWrap="wrap" mb={1.5}>
            <Numero cores={cores} titulo="Fichas no filtro" valor={resumo.fichas} />
            <Numero cores={cores} titulo="Vão receber" valor={resumo.comEmail} cor="success" ajuda="Com e-mail válido" />
            <Numero cores={cores} titulo="Ficam de fora" valor={resumo.semEmail} cor={resumo.semEmail ? "warning" : undefined} ajuda="Sem e-mail válido" />
            <Numero cores={cores} titulo="E-mails" valor={resumo.emails} ajuda="Destinatários (contam no limite da conta)" />
            {resumo.recebeuRecente > 0 && (
              <Numero cores={cores} titulo="Receberam há pouco" valor={resumo.recebeuRecente} ajuda={`Fora: mala direta nos últimos ${aplicado?.diasSemReceber} dias`} />
            )}
          </MDBox>
          <TableContainer sx={{ boxShadow: "none", border: `1px solid ${cores.divisoria}`, borderRadius: 1, maxHeight: 420 }}>
            <Table size="small" stickyHeader sx={{ minWidth: 1000 }}>
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  {["Ficha", "Código", "Devedor", "Cliente", "Carteira", "Situação · Status", "Responsável", "E-mails", ""].map((c) => (
                    <TableCell key={c} sx={celulaCab}>{c}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {resumo.amostra.length === 0 && (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ ...celula, py: 3 }}>Nenhuma ficha no filtro.</TableCell></TableRow>
                )}
                {resumo.amostra.map((a) => (
                  <TableRow key={a.ficha} hover>
                    <TableCell sx={celula}>#{a.ficha}</TableCell>
                    <TableCell sx={celula}>{textoOuTraco(a.codigo)}</TableCell>
                    <TableCell sx={{ ...celula, minWidth: 180 }}>{textoOuTraco(a.nome)}</TableCell>
                    <TableCell sx={celula}>{textoOuTraco(a.cliente)}</TableCell>
                    <TableCell sx={celula}>{textoOuTraco(a.carteira)}</TableCell>
                    <TableCell sx={celula}>{a.situacao}{a.status ? ` · ${a.status}` : ""}</TableCell>
                    <TableCell sx={celula}>{textoOuTraco(a.responsavel)}</TableCell>
                    <TableCell sx={{ ...celula, color: a.emails ? cores.texto : cores.textoFraco }}>{a.emails || "Sem e-mail válido"}</TableCell>
                    <TableCell sx={{ ...celula, pt: 0.5 }}>
                      <Tooltip title={modelo ? "Ver o e-mail desta ficha" : "Escolha o modelo para ver o e-mail"}>
                        <span>
                          <IconButton size="small" color="info" disabled={!modelo || !!ocupado} onClick={() => verPreview(a.ficha)}>
                            {ocupado === `preview-${a.ficha}` ? <CircularProgress size={16} color="inherit" /> : <Icon fontSize="small">visibility</Icon>}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <MDTypography variant="caption" color="text" display="block" mt={1}>
            {resumo.fichas > resumo.amostra.length
              ? `Mostrando as ${resumo.amostra.length} primeiras de ${numero(resumo.fichas)} fichas: a planilha de validação traz todas, com os valores do e-mail.`
              : `${numero(resumo.fichas)} ficha(s).`}
          </MDTypography>
        </Card>
      )}

      <Card sx={{ px: 2, py: 1.5, background: cores.cartao }}>
        {conta && !conta.configurada && (
          <Alert severity="warning" sx={{ mb: 1.5, fontSize: "0.85rem" }}>
            A conta de saída ainda não foi configurada (Ferramentas &gt; Parametrização do Sistema &gt; Mala Direta): sem ela, nada é enviado.
          </Alert>
        )}
        {conta?.ambienteTeste && (
          <Alert severity={conta.emailDeTeste ? "info" : "warning"} sx={{ mb: 1.5, fontSize: "0.85rem" }}>
            {conta.emailDeTeste
              ? <>Ambiente de teste: os e-mails dos lotes vão para <b>{conta.emailDeTeste}</b>, não para os devedores.</>
              : <>Ambiente de teste sem e-mail para receber os envios (app.mail.dev-override ou o seu e-mail no cadastro de usuário).</>}
          </Alert>
        )}
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={4}>
            <Rotulo>Modelo</Rotulo>
            <MDSelect name="modelo" placeholder="Escolha o modelo" options={modelosDoPublico} getOptionLabel={(o) => o.label} value={modelo} onChange={setModelo} />
            <MDTypography variant="caption" color="text" display="block" sx={{ px: 1, mt: 0.5 }}>
              {modelo?.assunto ? `Assunto: ${modelo.assunto}` : "Os universais e, com o público de uma carteira só, os dessa carteira (Mala Direta > Modelos de Mala Direta)."}
            </MDTypography>
          </Grid>
          <Grid item xs={12} md={2}>
            <Rotulo>Data de retorno</Rotulo>
            <MDInput size="small" type="date" value={dataRetorno} onChange={(e) => setDataRetorno(e.target.value)} fullWidth />
            <MDTypography variant="caption" color="text" display="block" sx={{ px: 1, mt: 0.5 }}>Para a variável [DataRetorno].</MDTypography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Rotulo>Anexo (opcional)</Rotulo>
            <MDBox display="flex" alignItems="center" gap={1}>
              <input type="file" ref={anexoRef} style={{ display: "none" }} onChange={(e) => { setAnexo(e.target.files?.[0] || null); e.target.value = ""; }} />
              <MDButton variant="outlined" color="info" size="medium" sx={{ height: 38, whiteSpace: "nowrap" }} onClick={() => anexoRef.current?.click()}>
                <Icon sx={{ mr: 0.5 }}>attach_file</Icon> Escolher
              </MDButton>
              <MDTypography variant="caption" color="text" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {anexo ? anexo.name : "Sem anexo"}
              </MDTypography>
              {anexo && <Icon fontSize="small" sx={{ cursor: "pointer", color: cores.textoFraco }} onClick={() => setAnexo(null)}>close</Icon>}
            </MDBox>
            <MDTypography variant="caption" color="text" display="block" sx={{ px: 1, mt: 0.5 }}>Vai igual para todos (até 10 MB).</MDTypography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Rotulo>Sai por</Rotulo>
            <MDInput select size="small" fullWidth value={remetente} onChange={(e) => setRemetente(e.target.value)} disabled={!conta?.configurada}>
              <MenuItem value="CONTA">{conta?.configurada ? `Conta padrão (${conta.email})` : "Conta padrão (não configurada)"}</MenuItem>
              <MenuItem value="RESPONSAVEL" disabled={!contaMicrosoft365}>E-mail do responsável da ficha</MenuItem>
            </MDInput>
            <MDTypography variant="caption" color="text" display="block" sx={{ px: 1, mt: 0.5 }}>
              {remetente === "RESPONSAVEL"
                ? (resumo && !alterado
                  ? `Cada ficha sai pela caixa do seu responsável: ${numero(resumo.comResponsavel)} de ${numero(resumo.comEmail)} ficha(s) prontas (${numero(resumo.responsaveis)} pessoa(s)).`
                  : "Cada ficha sai pela caixa do seu responsável: todos precisam ter e-mail cadastrado.")
                : !contaMicrosoft365 && conta?.configurada
                  ? "Para sair pelo e-mail do responsável, a conta de saída tem de ser do Microsoft 365."
                  : conta?.configurada ? `Até ${numero(conta.limitePorMinuto)} por minuto e ${numero(conta.limitePorDia)} destinatários por dia.` : ""}
            </MDTypography>
          </Grid>
          {pendenciasResponsavel && (
            <Grid item xs={12}>
              <Alert severity="warning" sx={{ fontSize: "0.85rem" }}>
                Para sair pelo e-mail do responsável, toda ficha precisa de responsável com e-mail cadastrado (não sai pela conta padrão).
                Acerte antes de enviar:
                <ul style={{ margin: "4px 0 0 18px" }}>
                  {(resumo.responsaveisSemEmail || []).map((r) => (
                    <li key={r.nome}><b>{r.nome}</b>: sem e-mail no cadastro de usuário ({numero(r.fichas)} ficha{r.fichas > 1 ? "s" : ""}). Cadastre em Ferramentas &gt; Usuários.</li>
                  ))}
                  {resumo.fichasSemResponsavel > 0 && (
                    <li>
                      <b>{numero(resumo.fichasSemResponsavel)} ficha(s) sem responsável</b>
                      {` (${(resumo.exemplosSemResponsavel || []).map((f) => `#${f}`).join(", ")}${resumo.fichasSemResponsavel > (resumo.exemplosSemResponsavel || []).length ? "..." : ""})`}:
                      defina o responsável na ficha (aba Dados da Carteira) ou tire essas fichas do filtro.
                    </li>
                  )}
                  {resumo.semPresente > 0 && (
                    <li>
                      <b>{numero(resumo.semPresente)} ficha(s) de responsável afastado</b> sem outro responsável presente com e-mail neste
                      público para enviar no lugar dele: inclua no filtro fichas de quem está trabalhando ou espere a volta.
                    </li>
                  )}
                </ul>
              </Alert>
            </Grid>
          )}
          {afastadosHoje.length > 0 && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ fontSize: "0.85rem" }}>
                Afastados hoje (Ferramentas &gt; Usuários &gt; Afastamentos):{" "}
                {afastadosHoje.map((a, i) => (
                  <span key={a.nome}>{i > 0 ? ", " : ""}<b>{a.nome}</b> ({numero(a.fichas)} ficha{a.fichas > 1 ? "s" : ""}, {a.quando})</span>
                ))}.
                {(resumo.substitutos || []).length > 0 && (
                  <>
                    {resumo.substitutos.length === 1
                      ? ` As fichas saem pela caixa de ${resumo.substitutos[0]}`
                      : ` As fichas saem pelas caixas de ${resumo.substitutos.join(", ")}, divididas entre eles`}
                    ; a ficha continua do responsável dela e as variáveis do responsável no e-mail (nome, e-mail, ramal, telefones) passam a
                    ser as de quem envia.
                  </>
                )}
              </Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={modoTeste} onChange={(e) => setModoTeste(e.target.checked)} />}
              label={<MDTypography variant="button" fontWeight="medium" sx={{ color: cores.texto }}>Envio de teste</MDTypography>}
            />
            <MDTypography variant="caption" color="text" display="block" sx={{ px: 1 }}>
              Manda 1 e-mail de cada conta que enviaria o lote, só para o e-mail de teste: pela conta padrão, a 1ª ficha do lote; pelo
              responsável, a 1ª ficha de cada responsável, saindo da caixa dele. Os devedores da lista não recebem e nada é gravado na ficha; o teste
              fica como lote de teste (Lotes enviados e, na Análise, com "Incluir envios de teste").
            </MDTypography>
          </Grid>
          {modoTeste && (
            <>
              <Grid item xs={12} md={4}>
                <Rotulo>E-mail de teste</Rotulo>
                <MDInput size="small" fullWidth value={emailTeste} onChange={(e) => setEmailTeste(e.target.value)} placeholder="seu.email@raadvogados.adv.br" />
                <MDTypography variant="caption" color="text" display="block" sx={{ px: 1, mt: 0.5 }}>Quem recebe o teste (no lugar do devedor).</MDTypography>
              </Grid>
            </>
          )}
          <Grid item xs={12} md={modoTeste ? 8 : 12}>
            <Rotulo>&nbsp;</Rotulo>
            <MDBox display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
              <MDButton variant="outlined" color="info" size="medium" sx={{ height: 38 }} onClick={() => verPreview(null)} disabled={!resumo || alterado || !modelo || !!ocupado}>
                {ocupado === "preview" ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.75 }} /> : <Icon sx={{ mr: 0.5 }}>visibility</Icon>}
                Pré-visualizar
              </MDButton>
              {modoTeste ? (
                <MDButton variant="contained" color="info" size="medium" sx={{ height: 38 }} onClick={enviarTeste}
                  disabled={!resumo || alterado || !modelo || !emailTeste || !conta?.configurada || pendenciasResponsavel || !!ocupado}>
                  {ocupado === "teste" ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.75 }} /> : <Icon sx={{ mr: 0.5 }}>outgoing_mail</Icon>}
                  {emailsNoTeste ? `Enviar teste (${numero(emailsNoTeste)} e-mail${emailsNoTeste > 1 ? "s" : ""})` : "Enviar teste"}
                </MDButton>
              ) : (
                <>
                  <MDButton variant="outlined" color="info" size="medium" sx={{ height: 38 }} onClick={baixarValidacao} disabled={!resumo || alterado || !!ocupado}>
                    {ocupado === "validacao" ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.75 }} /> : <Icon sx={{ mr: 0.5 }}>download</Icon>}
                    {ocupado === "validacao" ? "Gerando planilha..." : "Planilha de validação"}
                  </MDButton>
                  <MDButton variant="contained" color="info" size="medium" sx={{ height: 38 }} onClick={enviar} disabled={!podeEnviar || pendenciasResponsavel || !!ocupado}>
                    {ocupado === "envio" ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.75 }} /> : <Icon sx={{ mr: 0.5 }}>send</Icon>}
                    {resumo && !alterado ? `Enviar para ${numero(resumo.comEmail)} ficha(s)` : "Enviar"}
                  </MDButton>
                </>
              )}
            </MDBox>
            {!resumo && (
              <MDTypography variant="caption" color="text" display="block" sx={{ px: 1, mt: 0.5, textAlign: "right" }}>
                Aplique os filtros para montar o público.
              </MDTypography>
            )}
          </Grid>
        </Grid>
      </Card>

      {/* Quase a tela inteira: o e-mail aparece no tamanho em que chega (antes 900 px de largura e 55% da altura) */}
      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth={false}
        PaperProps={{ sx: { background: cores.cartao, width: "min(1100px, 96vw)", height: "94vh", maxWidth: "none", m: 1 } }}>
        <DialogTitle sx={{ color: cores.texto, pb: 1 }}>
          {/* O MDBox usa cor escura por padrão: no tema escuro o título sumia no cinza */}
          <MDBox display="flex" justifyContent="space-between" alignItems="center" sx={{ color: "inherit" }}>
            <span>Pré-visualização · ficha #{preview?.ficha}</span>
            <IconButton size="small" onClick={() => setPreview(null)}><Icon sx={{ color: cores.texto }}>close</Icon></IconButton>
          </MDBox>
        </DialogTitle>
        {preview && (
          <DialogContent sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <MDTypography variant="caption" display="block" sx={{ color: `${cores.textoSecundario} !important` }}>
              De: <b>{preview.de || "-"}</b>{preview.deAviso ? ` (${preview.deAviso})` : ""}
            </MDTypography>
            <MDTypography variant="caption" display="block" sx={{ color: `${cores.textoSecundario} !important` }}>
              Para: <b>{preview.para?.length ? preview.para.join(", ") : "sem e-mail válido (esta ficha fica de fora)"}</b>
            </MDTypography>
            <MDTypography variant="caption" display="block" mb={1} sx={{ color: `${cores.textoSecundario} !important` }}>
              Assunto: <b>{preview.assunto}</b>
            </MDTypography>
            <iframe title="Pré-visualização do e-mail" sandbox="" srcDoc={preview.html}
              style={{ width: "100%", flex: 1, minHeight: 0, border: `1px solid ${cores.divisoria}`, borderRadius: 6, background: CLARO.cartao }} />
            <MDTypography variant="caption" display="block" mt={1} sx={{ color: `${cores.textoSecundario} !important` }}>
              {preview.variaveis.filter((v) => v.valor === "").length > 0
                ? `Variáveis vazias nesta ficha: ${preview.variaveis.filter((v) => v.valor === "").map((v) => v.variavel).join(", ")}`
                : "Todas as variáveis têm valor nesta ficha."}
            </MDTypography>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
}

// ------------------------------------------------------------------ Lotes enviados

function LotesEnviados({ cores, loteInicial }) {
  const [lotes, setLotes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [detalhe, setDetalhe] = useState(null);
  const [situacaoDetalhe, setSituacaoDetalhe] = useState(null);

  const carregar = useCallback((silencioso) => {
    if (!silencioso) setCarregando(true);
    get("/mala-direta/lotes")
      .then((r) => setLotes(Array.isArray(r) ? r : []))
      .catch((e) => !silencioso && errorAlert(e?.message || "Erro ao carregar os lotes."))
      .finally(() => setCarregando(false));
  }, []);

  const abrir = useCallback((id, situacao) => {
    get(`/mala-direta/lotes/${id}${situacao ? `?situacao=${situacao}` : ""}`)
      .then(setDetalhe)
      .catch((e) => errorAlert(e?.message || "Erro ao abrir o lote."));
  }, []);

  useEffect(() => {
    carregar();
    if (loteInicial) abrir(loteInicial);
  }, [carregar, abrir, loteInicial]);

  const temAndando = lotes.some((l) => l.situacao === "ENVIANDO");
  useEffect(() => {
    if (!temAndando) return undefined;
    const t = setInterval(() => {
      carregar(true);
      if (detalhe?.situacao === "ENVIANDO") abrir(detalhe.id, situacaoDetalhe?.value);
    }, 10000);
    return () => clearInterval(t);
  }, [temAndando, carregar, abrir, detalhe, situacaoDetalhe]);

  const acao = (lote, nome, confirmar) => {
    const executar = () => post(`/mala-direta/lotes/${lote.id}/${nome}`, {})
      .then((r) => {
        successAlert(r?.message || "Feito.");
        carregar(true);
        if (detalhe?.id === lote.id) abrir(lote.id, situacaoDetalhe?.value);
      })
      .catch((e) => errorAlert(e?.message || "Erro."));
    if (confirmar) infoAviso(confirmar, executar);
    else executar();
  };

  const planilha = (lote) => postUploadFile(`/mala-direta/lotes/${lote.id}/exportar`, new FormData(), `MALA_DIRETA_LOTE_${lote.id}.xlsx`)
    .catch((e) => errorAlert(e?.message || "Erro ao gerar a planilha."));

  const celulaCab = { fontWeight: "bold", fontSize: "0.75rem", backgroundColor: cores.painel, color: cores.texto, whiteSpace: "nowrap", py: 1 };
  const celula = { fontSize: "0.8rem", color: cores.texto, verticalAlign: "top", py: 1 };
  const chip = (mapa, valor) => {
    const s = mapa[valor] || { label: valor, cor: "default" };
    return <Chip size="small" variant="outlined" color={s.cor} label={s.label} sx={{ height: 20, fontSize: "0.7rem" }} />;
  };
  const andamento = (l) => {
    const alvo = l.enviados + l.erros + l.pendentes;
    return alvo > 0 ? Math.round(((l.enviados + l.erros) * 100) / alvo) : 100;
  };

  const botoes = (l) => (
    <MDBox display="flex" gap={0.25}>
      <Tooltip title="Ver os envios"><IconButton size="small" color="info" onClick={() => { setSituacaoDetalhe(null); abrir(l.id); }}><Icon fontSize="small">visibility</Icon></IconButton></Tooltip>
      {/* Lote de teste (chave "Envio de teste"): só ver e baixar; o backend também recusa as ações */}
      {!l.teste && l.situacao === "ENVIANDO" && <Tooltip title="Pausar"><IconButton size="small" color="warning" onClick={() => acao(l, "pausar")}><Icon fontSize="small">pause_circle</Icon></IconButton></Tooltip>}
      {!l.teste && l.situacao === "PAUSADO" && <Tooltip title="Retomar"><IconButton size="small" color="success" onClick={() => acao(l, "retomar")}><Icon fontSize="small">play_circle</Icon></IconButton></Tooltip>}
      {!l.teste && (l.situacao === "ENVIANDO" || l.situacao === "PAUSADO") && (
        <Tooltip title="Cancelar o que está na fila">
          <IconButton size="small" color="error" onClick={() => acao(l, "cancelar", `Cancelar o lote nº ${l.id}? As ${numero(l.pendentes)} ficha(s) ainda na fila não recebem.`)}>
            <Icon fontSize="small">cancel</Icon>
          </IconButton>
        </Tooltip>
      )}
      {!l.teste && l.erros > 0 && l.situacao !== "CANCELADO" && (
        <Tooltip title="Reenviar os envios com erro">
          <IconButton size="small" color="info" onClick={() => acao(l, "reenviar-erros", `Pôr de volta na fila os ${numero(l.erros)} envio(s) com erro do lote nº ${l.id}?`)}>
            <Icon fontSize="small">replay</Icon>
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Planilha do lote"><IconButton size="small" color="info" onClick={() => planilha(l)}><Icon fontSize="small">download</Icon></IconButton></Tooltip>
    </MDBox>
  );

  return (
    <Card sx={{ p: 2, background: cores.cartao }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5} gap={1} flexWrap="wrap">
        <MDTypography variant="caption" color="text">
          A fila manda no ritmo da conta de saída e grava cada envio na hora{temAndando ? " (atualiza a cada 10 s)" : ""}. Os 100 lotes mais recentes.
        </MDTypography>
        <MDButton variant="contained" color="info" size="medium" onClick={() => carregar()} disabled={carregando}>
          <Icon sx={{ mr: 0.5 }}>refresh</Icon> Atualizar
        </MDButton>
      </MDBox>
      <TableContainer sx={{ boxShadow: "none", border: `1px solid ${cores.divisoria}`, borderRadius: 1 }}>
        <Table size="small" sx={{ minWidth: 1350 }}>
          <TableHead sx={{ display: "table-header-group" }}>
            <TableRow>
              {["Nº", "Criado em", "Por", "Modelo", "Situação", "Andamento", "Enviados", "Entregues", "Não entregues", "Abertos", "Erros", "Na fila", "Sem e-mail", ""].map((c) => (
                <TableCell key={c} sx={celulaCab}>{c}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {lotes.length === 0 && (
              <TableRow><TableCell colSpan={14} align="center" sx={{ ...celula, py: 3 }}>{carregando ? "Carregando..." : "Nenhum lote enviado ainda."}</TableCell></TableRow>
            )}
            {lotes.map((l) => (
              <TableRow key={l.id} hover>
                <TableCell sx={celula}>{l.id}</TableCell>
                <TableCell sx={{ ...celula, whiteSpace: "nowrap" }}>{l.criadoEm}</TableCell>
                <TableCell sx={celula}>{textoOuTraco(l.usuario)}</TableCell>
                <TableCell sx={{ ...celula, minWidth: 160 }}>{textoOuTraco(l.modelo)}</TableCell>
                <TableCell sx={{ ...celula, minWidth: 230 }}>
                  {chip(SITUACAO_LOTE, l.situacao)}
                  {l.teste && (
                    <Tooltip title="Chave &quot;Envio de teste&quot;: um e-mail por conta, só para o e-mail de teste, sem ocorrência na ficha. Na Análise, com &quot;Incluir envios de teste&quot;.">
                      <Chip size="small" variant="outlined" color="warning" label="Envio de teste" sx={{ height: 20, fontSize: "0.7rem", ml: 0.5 }} />
                    </Tooltip>
                  )}
                  {l.aviso && <MDTypography variant="caption" display="block" color="warning" mt={0.5} sx={{ maxWidth: 260 }}>{l.aviso}</MDTypography>}
                </TableCell>
                <TableCell sx={{ ...celula, minWidth: 120 }}>
                  <LinearProgress variant="determinate" value={andamento(l)} color={l.erros > 0 ? "warning" : "success"} sx={{ height: 6, borderRadius: 3, mt: 0.75 }} />
                  <MDTypography variant="caption" sx={{ color: cores.textoSecundario }}>{andamento(l)}%</MDTypography>
                </TableCell>
                <TableCell sx={celula}>{numero(l.enviados)}</TableCell>
                <TableCell sx={celula}>
                  {l.medeRetorno ? numero(l.entregues) : "-"}
                  {l.medeRetorno && l.aguardando > 0 && (
                    <MDTypography variant="caption" display="block" sx={{ color: cores.textoSecundario }}>+{numero(l.aguardando)} aguardando</MDTypography>
                  )}
                </TableCell>
                <TableCell sx={{ ...celula, color: l.naoEntregues ? "error.main" : cores.texto }}>
                  {l.medeRetorno ? numero(l.naoEntregues) : "-"}
                  {l.marcadosRuins > 0 && (
                    <MDTypography variant="caption" display="block" sx={{ color: cores.textoSecundario }}>{numero(l.marcadosRuins)} marcado(s) ruim</MDTypography>
                  )}
                </TableCell>
                <TableCell sx={{ ...celula, whiteSpace: "nowrap" }}>
                  {l.medeAbertura ? `${numero(l.aberturas)}${l.taxaAbertura != null ? ` (${String(l.taxaAbertura).replace(".", ",")}%)` : ""}` : "-"}
                  {l.medeClique && (
                    <MDTypography variant="caption" display="block" sx={{ color: cores.textoSecundario }}>
                      {numero(l.cliques)} com clique{l.taxaClique != null ? ` (${String(l.taxaClique).replace(".", ",")}%)` : ""}
                    </MDTypography>
                  )}
                </TableCell>
                <TableCell sx={{ ...celula, color: l.erros ? "error.main" : cores.texto }}>{numero(l.erros)}</TableCell>
                <TableCell sx={celula}>{numero(l.pendentes)}</TableCell>
                <TableCell sx={celula}>{numero(l.semEmail)}</TableCell>
                <TableCell sx={{ ...celula, pt: 0.5 }}>{botoes(l)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <MDTypography variant="caption" display="block" mt={1} sx={{ color: cores.textoSecundario }}>
        Entregues e não entregues contam e-mails (uma ficha pode ter mais de um). Não entregue = o servidor do destinatário devolveu
        o aviso de não entrega (lido nas caixas que enviaram); entregue = sem aviso em 48 h. O e-mail que não existe vira "ruim" na
        ficha, com o motivo. Abertos: aproximado (contados por lote, sem identificar o devedor; o Apple Mail abre as imagens sozinho e o
        Outlook as bloqueia). "-" = lote enviado antes da medição.
      </MDTypography>

      <Dialog open={!!detalhe} onClose={() => setDetalhe(null)} maxWidth="lg" fullWidth PaperProps={{ sx: { background: cores.cartao } }}>
        {detalhe && (
          <>
            <DialogTitle sx={{ color: cores.texto, pb: 1 }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" sx={{ color: "inherit" }}>
                <span>Lote nº {detalhe.id} · {detalhe.modelo}</span>
                <IconButton size="small" onClick={() => setDetalhe(null)}><Icon sx={{ color: cores.texto }}>close</Icon></IconButton>
              </MDBox>
            </DialogTitle>
            <DialogContent>
              <MDTypography variant="caption" display="block" sx={{ color: cores.textoSecundario }}>
                Criado em {detalhe.criadoEm} por {textoOuTraco(detalhe.usuario)}{detalhe.fim ? ` · terminou em ${detalhe.fim}` : ""} · Assunto: {textoOuTraco(detalhe.assunto)}
                {detalhe.dataRetorno ? ` · Data de retorno: ${detalhe.dataRetorno}` : ""}{detalhe.anexo ? ` · Anexo: ${detalhe.anexo}` : ""}
                {detalhe.remetente ? ` · Sai por: ${detalhe.remetente}` : ""}
              </MDTypography>
              <MDTypography variant="caption" display="block" mb={1} sx={{ color: cores.textoSecundario }}>Filtros: {textoOuTraco(detalhe.filtros)}</MDTypography>
              <MDBox display="flex" gap={1} alignItems="center" mb={1} flexWrap="wrap">
                {chip(SITUACAO_LOTE, detalhe.situacao)}
                <MDTypography variant="caption" sx={{ color: cores.textoSecundario }}>
                  {numero(detalhe.enviados)} enviado(s) · {numero(detalhe.erros)} com erro · {numero(detalhe.pendentes)} na fila · {numero(detalhe.semEmail)} sem e-mail
                  {detalhe.cancelados ? ` · ${numero(detalhe.cancelados)} cancelado(s)` : ""}
                  {detalhe.medeRetorno
                    ? ` · E-mails: ${numero(detalhe.entregues)} entregue(s), ${numero(detalhe.naoEntregues)} não entregue(s)${detalhe.marcadosRuins ? ` (${numero(detalhe.marcadosRuins)} marcado(s) ruim)` : ""}${detalhe.aguardando ? `, ${numero(detalhe.aguardando)} aguardando retorno` : ""}`
                    : ""}
                  {detalhe.medeAbertura ? ` · Abertos: ${numero(detalhe.aberturas)}${detalhe.taxaAbertura != null ? ` (${String(detalhe.taxaAbertura).replace(".", ",")}%)` : ""}` : ""}
                </MDTypography>
                <MDBox sx={{ width: 220, ml: "auto" }}>
                  <MDSelect name="situacaoEnvio" isClearable placeholder="Todas as situações"
                    options={[
                      ...Object.entries(SITUACAO_ENVIO).filter(([k]) => k !== "ENVIANDO").map(([value, s]) => ({ value, label: s.label })),
                      ...(detalhe.medeRetorno ? [{ value: "NAO_ENTREGUE", label: "Não entregue (voltou)" }] : []),
                    ]}
                    getOptionLabel={(o) => o.label} value={situacaoDetalhe}
                    onChange={(o) => { setSituacaoDetalhe(o); abrir(detalhe.id, o?.value); }} />
                </MDBox>
              </MDBox>
              {(detalhe.errosRetorno || []).map((e) => (
                <MDTypography key={e} variant="caption" display="block" color="warning" mb={0.5}>{e}</MDTypography>
              ))}
              <TableContainer sx={{ boxShadow: "none", border: `1px solid ${cores.divisoria}`, borderRadius: 1, maxHeight: "55vh" }}>
                <Table size="small" stickyHeader sx={{ minWidth: 1150 }}>
                  <TableHead sx={{ display: "table-header-group" }}>
                    <TableRow>
                      {["Situação", "Entrega", "Ficha", "Código", "Devedor", "Cliente · Carteira", "E-mails", "De", "Enviado em", "Erro / não entregue"].map((c) => (
                        <TableCell key={c} sx={celulaCab}>{c}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detalhe.envios.length === 0 && (
                      <TableRow><TableCell colSpan={10} align="center" sx={{ ...celula, py: 3 }}>Nenhum envio nesta situação.</TableCell></TableRow>
                    )}
                    {detalhe.envios.map((e) => (
                      <TableRow key={e.id} hover>
                        <TableCell sx={celula}>{chip(SITUACAO_ENVIO, e.situacao)}</TableCell>
                        <TableCell sx={celula}>{e.entrega ? chip(ENTREGA, e.entrega) : "-"}</TableCell>
                        <TableCell sx={celula}>{e.ficha ? `#${e.ficha}` : "-"}</TableCell>
                        <TableCell sx={celula}>{textoOuTraco(e.codigo)}</TableCell>
                        <TableCell sx={{ ...celula, minWidth: 160 }}>{textoOuTraco(e.nome)}</TableCell>
                        <TableCell sx={celula}>{textoOuTraco(e.cliente)}{e.carteira ? ` · ${e.carteira}` : ""}</TableCell>
                        <TableCell sx={celula}>{textoOuTraco(e.emails)}</TableCell>
                        <TableCell sx={celula}>{textoOuTraco(e.de)}</TableCell>
                        <TableCell sx={{ ...celula, whiteSpace: "nowrap" }}>{textoOuTraco(e.enviadoEm)}</TableCell>
                        <TableCell sx={{ ...celula, color: e.erro || e.retornos?.length ? "error.main" : cores.texto, minWidth: 220 }}>
                          {e.erro || (!e.retornos?.length && "-")}
                          {(e.retornos || []).map((x) => (
                            <MDBox key={x.email} sx={{ color: "inherit" }}>
                              {x.email}: {x.motivo}{x.marcouRuim ? " · marcado como ruim" : ""}
                            </MDBox>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <MDTypography variant="caption" display="block" mt={1} sx={{ color: cores.textoSecundario }}>
                {detalhe.enviosTotal > detalhe.envios.length
                  ? `Mostrando ${numero(detalhe.envios.length)} de ${numero(detalhe.enviosTotal)}: a planilha do lote traz todos.`
                  : `${numero(detalhe.enviosTotal)} envio(s).`}
              </MDTypography>
              <MDBox display="flex" justifyContent="flex-end" gap={1} mt={1}>{botoes(detalhe)}</MDBox>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Card>
  );
}

// ------------------------------------------------------------------ tela

export default function MalaDiretaLote() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const cores = useMemo(() => coresDoTema(darkMode), [darkMode]);
  const [aba, setAba] = useState(0);
  const [loteInicial, setLoteInicial] = useState(null);

  return (
    <MDBox pt={1} pb={3}>
      <Card sx={{ px: 2, pt: 0.5, mb: 2, background: cores.cartao }}>
        <Tabs value={aba} onChange={(e, nova) => { setAba(nova); setLoteInicial(null); }} textColor={darkMode ? "inherit" : "primary"} indicatorColor="info">
          <Tab label="Novo envio" sx={{ fontSize: 12, color: darkMode ? (aba === 0 ? "#fff" : "rgba(255, 255, 255, 0.7)") : "inherit" }} />
          <Tab label="Lotes enviados" sx={{ fontSize: 12, color: darkMode ? (aba === 1 ? "#fff" : "rgba(255, 255, 255, 0.7)") : "inherit" }} />
        </Tabs>
      </Card>
      {aba === 0 && <NovoEnvio cores={cores} aoCriarLote={(id) => { setLoteInicial(id); setAba(1); }} />}
      {aba === 1 && <LotesEnviados cores={cores} loteInicial={loteInicial} />}
    </MDBox>
  );
}
